// Playground local para iterar UX de pantallas del wizard. Vive detrás de guard: en build de prod
// `import.meta.env.PROD === true` y lanzamos notFound() → la ruta no se expone en producción.
//
// AISLAMIENTO: el PhoneSelectStep llama internamente `useSearch({ from: '/wizard' })`, por eso
// construyo acá un sub-router in-memory con el árbol mínimo que el componente espera (`/wizard` →
// `/wizard/s/$sessionId/$step`). Esto permite renderizar el componente REAL sin montarlo bajo el
// router principal (que requiere auth) y sin tocar el código de prod.
//
// Para iterar los otros pasos del wizard: duplicar este archivo o reutilizar el `makeSubRouter`
// cambiando el `component` del `stepRoute` por el step correspondiente.
import {
    Outlet,
    RouterProvider,
    createFileRoute,
    createMemoryHistory,
    createRootRoute,
    createRoute,
    createRouter,
    notFound,
} from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { PhoneSelectStep } from '@/domains/sales/wizard/steps/PhoneSelectStep';
import { WizardShell } from '@/domains/sales/wizard/components/WizardShell';
import { WIZARD_KEYS } from '@/domains/sales/wizard/api/useWizard';
import {
    WIZARD_PROMOTIONS_KEY,
    storePromotionsKey,
} from '@/domains/retail/promotions-wizard/api/useWizardPromotions';
import { buildPromotionWizardSlots } from '@/domains/retail/promotions-wizard/slots/promotionWizardSlots';
import { useSessionPromotionState } from '@/domains/retail/promotions-wizard/store/promotionSelectionStore';
import {
    CATALOG_FIXTURE,
    DEV_SESSION_ID,
    SESSION_FIXTURE,
    SKU_PROMOTIONS_FIXTURE_BY_SKU,
    STORE_SALE_PROMOTIONS_FIXTURE,
} from '@/dev/wizard-playground/fixtures';

export const Route = createFileRoute('/dev/wizard-playground')({
    beforeLoad: () => {
        if (import.meta.env.PROD) throw notFound();
    },
    component: WizardPlayground,
});

function makeSubRouter(sessionId: string) {
    const rootRoute = createRootRoute({ component: () => <Outlet /> });
    const wizardRoute = createRoute({
        getParentRoute: () => rootRoute,
        path: 'wizard',
        validateSearch: (
            search: Record<string, unknown>,
        ): { sku?: string; phone?: string; prospect?: string; accountOpening?: true } => ({
            sku: typeof search.sku === 'string' && search.sku !== '' ? search.sku : undefined,
            phone:
                typeof search.phone === 'string' && search.phone !== '' ? search.phone : undefined,
            prospect:
                typeof search.prospect === 'string' && search.prospect !== ''
                    ? search.prospect
                    : undefined,
            accountOpening: search.accountOpening === true ? true : undefined,
        }),
        component: () => <Outlet />,
    });
    const sessionRoute = createRoute({
        getParentRoute: () => wizardRoute,
        path: 's/$sessionId',
        component: () => (
            <WizardShell sessionId={sessionId}>
                <Outlet />
            </WizardShell>
        ),
    });
    const stepRoute = createRoute({
        getParentRoute: () => sessionRoute,
        path: '$step',
        component: () => <PlaygroundPhoneSelect sessionId={sessionId} />,
    });
    const routeTree = rootRoute.addChildren([
        wizardRoute.addChildren([sessionRoute.addChildren([stepRoute])]),
    ]);
    return createRouter({
        routeTree,
        history: createMemoryHistory({
            initialEntries: [`/wizard/s/${sessionId}/product`],
        }),
        defaultPreload: false,
    });
}

/** El 04 con las mismas piezas de Promociones que el wizard, sin depender de la URL configurada. */
function PlaygroundPhoneSelect({ sessionId }: { sessionId: string }) {
    const queryClient = useQueryClient();
    const { phoneDraft } = useSessionPromotionState(sessionId);
    const slots = useMemo(
        () =>
            buildPromotionWizardSlots({
                sessionId,
                queryClient,
                storePromotions: STORE_SALE_PROMOTIONS_FIXTURE.items,
                ...(phoneDraft && { phoneDraft }),
            }).phoneSelect ?? {},
        [sessionId, queryClient, phoneDraft],
    );
    return <PhoneSelectStep sessionId={sessionId} slots={slots} />;
}

function makeContext() {
    const q = new QueryClient({
        defaultOptions: { queries: { retry: false, staleTime: Infinity } },
    });
    q.setQueryData(WIZARD_KEYS.session(DEV_SESSION_ID), SESSION_FIXTURE);
    q.setQueryData(WIZARD_KEYS.catalog(DEV_SESSION_ID), CATALOG_FIXTURE);
    q.setQueryData(storePromotionsKey(DEV_SESSION_ID), STORE_SALE_PROMOTIONS_FIXTURE);
    for (const [sku, response] of Object.entries(SKU_PROMOTIONS_FIXTURE_BY_SKU)) {
        q.setQueryData(
            [...WIZARD_PROMOTIONS_KEY, 'by-sku', DEV_SESSION_ID, sku] as const,
            response,
        );
    }
    return { qc: q, subRouter: makeSubRouter(DEV_SESSION_ID) };
}

function WizardPlayground() {
    // useState con initializer: QueryClient estable para la vida del componente.
    const [{ qc, subRouter }] = useState(makeContext);

    return (
        <div className="min-h-screen bg-background">
            <div className="sticky top-0 z-50 border-b border-border bg-amber-50/90 px-4 py-2 text-xs backdrop-blur">
                <span className="font-semibold uppercase tracking-wide text-amber-900">
                    Playground · PhoneSelectStep
                </span>
                <span className="ml-2 text-amber-800">
                    Fixtures locales, cero backend. Edita el step y HMR actualiza.
                </span>
            </div>
            <QueryClientProvider client={qc}>
                <RouterProvider router={subRouter} />
            </QueryClientProvider>
        </div>
    );
}
